import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Send, AlertCircle, CheckCircle, ImageIcon } from "lucide-react";
import { API } from "../lib/constants";
import { useUser } from "../contexts/UserContext";
import AuthModal from "../components/AuthModal";
import WitnessStandTabs from "../components/WitnessStandTabs";

export default function WitnessStandWrite() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [genres, setGenres] = useState([]);
  const [form, setForm] = useState({ title: "", subtitle: "", genre: "", cover_url: "", content: "" });
  const [status, setStatus] = useState("draft"); // draft | pending | approved | rejected
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    fetch(`${API}/witness-stand/genres`).then(r => r.ok ? r.json() : { genres: [] }).then(d => setGenres(d.genres || []));
  }, []);

  useEffect(() => {
    if (!storyId) return;
    const token = localStorage.getItem("user_session_token");
    fetch(`${API}/witness-stand/stories/${storyId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.id) {
          setForm({
            title: d.title || "",
            subtitle: d.subtitle || "",
            genre: d.genre || "",
            cover_url: d.cover_url || "",
            content: d.content || "",
          });
          setStatus(d.status);
        }
      });
  }, [storyId]);

  const wordCount = form.content.trim() ? form.content.trim().split(/\s+/).length : 0;
  const minOk = form.content.length >= 300;
  const titleOk = form.title.trim().length >= 3;
  const genreOk = !!form.genre;

  const submit = async (publish) => {
    if (!user) { setShowAuth(true); return; }
    setErr(""); setOkMsg("");
    if (!titleOk) { setErr("Title needs at least 3 characters"); return; }
    if (!genreOk) { setErr("Pick a genre"); return; }
    if (!minOk) { setErr("Story content must be at least 300 characters"); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem("user_session_token");
      const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const method = storyId ? "PUT" : "POST";
      const url = storyId ? `${API}/witness-stand/stories/${storyId}` : `${API}/witness-stand/stories`;
      const r = await fetch(url, { method, headers, credentials: "include", body: JSON.stringify({ ...form, publish }) });
      if (!r.ok) {
        let msg = "Save failed";
        try { const d = await r.json(); msg = d.detail || msg; } catch {}
        throw new Error(msg);
      }
      const d = await r.json();
      if (publish) {
        setOkMsg("Submitted for review! Admin will approve shortly.");
        setStatus("pending");
      } else {
        setOkMsg("Saved as draft.");
      }
      // If creating new, redirect to edit URL
      if (!storyId && d.id) navigate(`/witness-stand/write/${d.id}`, { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
      setTimeout(() => setOkMsg(""), 4000);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="witness-stand-write">
      <div className="max-w-3xl mx-auto">
        <WitnessStandTabs />
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="font-mono text-red-500 text-xs tracking-[0.3em] mb-1">{storyId ? "EDIT STORY" : "PUBLISH A STORY"}</p>
            <h1 className="font-cinzel text-2xl md:text-3xl font-bold">{storyId ? "Edit Your Story" : "Tell Your Story"}</h1>
          </div>
        </div>

        {!user && (
          <div className="mb-6 p-4 bg-white/[0.03] border border-white/10 rounded-xl text-center text-sm text-gray-400" data-testid="write-auth-prompt">
            <button onClick={() => setShowAuth(true)} className="text-red-400 hover:text-red-300 font-bold" data-testid="write-signin-btn">Sign in</button> to save and publish your story.
          </div>
        )}

        {status !== "draft" && status !== undefined && (
          <div className={`mb-6 px-4 py-3 rounded-xl text-sm font-mono border ${
            status === "approved" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" :
            status === "pending"  ? "bg-amber-500/10 border-amber-500/30 text-amber-300" :
            status === "rejected" ? "bg-red-500/10 border-red-500/30 text-red-300" : ""
          }`} data-testid="story-status">
            STATUS: {status.toUpperCase()}
          </div>
        )}

        <div className="space-y-5">
          <div>
            <label className="block text-xs font-mono text-gray-500 mb-1">TITLE</label>
            <input
              type="text" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={140}
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 rounded-lg text-base font-cinzel text-white focus:outline-none focus:border-red-500/50"
              data-testid="story-title"
              placeholder="The Last Signature on Riverside Drive"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-gray-500 mb-1">SUBTITLE / LOGLINE (OPTIONAL)</label>
            <input
              type="text" value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              maxLength={240}
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-red-500/50"
              data-testid="story-subtitle"
              placeholder="A widow asks for a witness. The room knows something I don't."
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono text-gray-500 mb-1">GENRE</label>
              <select value={form.genre}
                      onChange={(e) => setForm({ ...form, genre: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-red-500/50"
                      data-testid="story-genre">
                <option value="">— pick one —</option>
                {genres.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono text-gray-500 mb-1">COVER IMAGE URL (OPTIONAL)</label>
              <div className="relative">
                <ImageIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="url" value={form.cover_url}
                  onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
                  className="w-full pl-9 pr-4 py-3 bg-[#0a0a0a] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-red-500/50"
                  data-testid="story-cover-url"
                  placeholder="https://images.unsplash.com/…"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono text-gray-500 mb-1">STORY</label>
            <textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={18}
              className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-red-500/50 leading-relaxed"
              data-testid="story-content"
              placeholder={`Write your story here. Markdown is fine.\n\nMin 300 characters.`}
            />
            <p className={`text-[11px] mt-1 font-mono ${minOk ? "text-emerald-400" : "text-gray-500"}`}>
              {form.content.length} / 300 characters · {wordCount} words
            </p>
          </div>

          {err && (
            <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="write-error">
              <AlertCircle size={14}/>{err}
            </div>
          )}
          {okMsg && (
            <div className="flex items-center gap-2 text-emerald-400 text-sm" data-testid="write-ok">
              <CheckCircle size={14}/>{okMsg}
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={() => submit(false)} disabled={busy}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-gray-200 font-bold disabled:opacity-50"
                    data-testid="save-draft-btn">
              <Save size={14}/>Save Draft
            </button>
            <button onClick={() => submit(true)} disabled={busy}
                    className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm text-white font-bold disabled:opacity-50"
                    data-testid="submit-publish-btn">
              <Send size={14}/>Submit for Review
            </button>
          </div>
        </div>
      </div>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
}
